import { IEntity, IOwner, SubmissionStatuses, UserType, UUID } from ".";

export interface IHttpsCallableResponse {
  success: boolean;
  message: string;
  location?: string;
}

export interface IHttpsCallableSuccessResponse extends IHttpsCallableResponse {
  success: true;
}

export interface IHttpsCallableFailResponse extends IHttpsCallableResponse {
  success: false;
}

export interface IUserInviteRequest {
  to: string;
  orgId: string;
  orgName: string;
  tempUserId: string;
  senderEmail: string;
}

export interface IUserInviteResponse extends IHttpsCallableResponse {
  message: string;
}

export interface IReplaceTempUserIdRequest {
  tempUserId: string;
  userId: UUID;
  orgId: UUID;
  email: string;
  userDisplayName: string;
}

export interface IReplaceTempUserIdResponse extends IHttpsCallableResponse {
  message: string;
}

export interface ICheckIfEmailInUseRequest {
  email: string;
}

export interface ICheckIfEmailInUseResponse extends IHttpsCallableResponse {
  isEmailInUse: boolean;
}

export interface ICheckActiveInvitationRequest {
  email: string;
}

export interface ICheckActiveInvitationResponse extends IHttpsCallableResponse {
  doesInvitationExist: boolean;
}

export interface IValidateUserInviteRequest {
  tempUserId: UUID;
  orgId: UUID;
}

export interface IValidateUserInviteResponse extends IHttpsCallableResponse {
  message: string;
}

export interface IVerifyDobRequest {
  ownerId: UUID;
  entityId: UUID;
  groupId: UUID;
  orgId: UUID;
  dob: string;
}

export interface IVerifyDobResponse {
  success: boolean;
  message: IVerifyDobResponseOwner | string;
}

export type IVerifyDobResponseOwner = IOwner;

export interface IMirrorOwnerRequest {
  orgId: UUID;
  ownerId: UUID;
  entityId: UUID;
}

export interface IUploadImageRequest {
  token?: string;
  image: string;
  mediaType: string;
  userType: UserType | null;
  orgId?: string;
  // entityId is required if the userType is "secure-file"
  entityId?: string;
  ownerId?: string;
}

export interface IValidateBulkEntityFile {
  orgId: UUID;
  groupId: UUID;
  mediaType: string;
  file: string;
}

export interface IValidateBulkEntityResponse extends IHttpsCallableResponse {
  entities: IValidatedBulkUploadEntity[];
}

export type IBulkUploadEntity = Pick<
  IEntity,
  | "legalName"
  | "tradeNames"
  | "taxIdType"
  | "taxId"
  | "stateOfFormation"
  | "address"
>;

export type IBulkUploadEntityValidationMessages = {
  [Key in Exclude<keyof IBulkUploadEntity, "address">]?: string[];
} & {
  address?: {
    [Key in keyof IBulkUploadEntity["address"]]?: string[];
  };
};

export interface IValidatedBulkUploadEntity {
  entity: IBulkUploadEntity | null;
  errors: IBulkUploadEntityValidationMessages | null;
  warns: IBulkUploadEntityValidationMessages | null;
}

export interface IBulkEntityQueueItem extends IBulkUploadEntity {
  id: UUID;
  groupId: UUID;
  groupName: string;
  userId: UUID;
}

export interface IUploadBulkEntities {
  userId: string;
  orgId: string;
  groupId: string;
  entities: IValidatedBulkUploadEntity[];
}

export interface IManualIdUploadDialogDataOutput {
  image: string;
  mediaType: string;
}

export interface IMoveEntityGroupRequest {
  orgId: string;
  entityId: string;
  entityLegalName: string;
  fromGroupId: string | null;
  toGroupId: string;
}

export interface ICanBeHandedOffInstantlyRequest {
  email: string
}

export interface ICanBeHandedOffInstantlyResponse extends IHttpsCallableResponse {
  userId?: string;
}

export type ISelfBillBulkSubscription =
  | "SecureProSelfBillSubscriptionBulk50"
  | "SecureProSelfBillSubscriptionBulk60"
  | "SecureProSelfBillSubscriptionBulk65"
  | "SecureProSelfBillSubscriptionBulk70"
  | "SecureProSelfBillSubscriptionBulk80";

export type ICheckoutType =
  | "SecureProSetupFee"
  | "SecureFileExemptionCertificate"
  | "SecureFileEntitySubscription"
  | "SecureProSelfBillSubscription"
  | "SecureProSelfBillSubscriptionBulk50"
  | "SecureProSelfBillSubscriptionBulk60"
  | "SecureProSelfBillSubscriptionBulk70"
  | "SecureProSelfBillSubscriptionBulk80"
  | "SecureProSelfBillSubscriptionBulk65"
  | "SecureFileEntitySubscriptionAndSetupFee";

export type ISelfBillCheckoutType =
  | "SecureProSelfBillSubscription"
  | ISelfBillBulkSubscription;

export interface IPaymentSession {
  session_id: string;
  client_secret: string;
}

export interface ICheckStripeSessionStatusResponse
  extends IHttpsCallableResponse {
  payload: {
    status: "open" | "complete" | "expired";
    paymentStatus: "paid" | "unpaid" | "no_payment_required";
  };
}

export interface IListPaymentMethodsResponse extends IHttpsCallableResponse {
  data: [];
}

export interface ICreateSubscriptionResponse extends IHttpsCallableResponse {
  subscriptionId: string;
  payload: {
    status:
    | "active"
    | "canceled"
    | "incomplete"
    | "incomplete_expired"
    | "past_due"
    | "paused"
    | "trialing"
    | "unpaid";
  };
}

export interface IInvoiceResponse {
  id: string;
  period_end: number;
  total: number;
  invoice_pdf: string | null;
  hosted_invoice_url: string | null;
  number: string | null;
  receipt_number: string | null;
  subscription_details: {
    metadata: {
      entityName?: string;
      entityId?: string;
    };
  };
  metadata: {
    entityName?: string;
    legalName?: string;
    entityId?: string;
    orgId?: string;
  };
}

export interface IInvoice {
  date: string;
  amount: string;
  entityName: string;
  invoiceId: string;
}

export interface IListInvoicesResponse extends IHttpsCallableResponse {
  success: boolean;
  message: string;
  data: IInvoiceResponse[];
}

export interface IUpsertProEntityRequest {
  orgId: string;
  newGroupId: string;
  existingGroupId?: string;
  entityId: string;
  entity: IEntity;
  ownerImages: { [ownerId: UUID]: IUploadImageRequest };
  updatedOwnerIds: UUID[];
}

export interface IUpsertFileEntityRequest {
  entityId: UUID;
  entity: IEntity;
  updatedOwnerIds: UUID[];
}

export interface ICheckoutSessionRequest {
  checkoutType: ICheckoutType;
  entityId: string | null;
  couponCode: string | null;
  userType: UserType | null;
  entityName: string | null;
}

export interface IBoirStatusResponse {
  processId: string;
  submissionStatus: SubmissionStatuses;
  errors?: {
    ErrorText?: string;
    ErrorCode?: string;
  }[];
  validationErrors?: {
    errorMessage?: string;
  }[];
  fincenID?: string;
  BOIRID?: string;
}

export interface PaginatedData<T> {
  length: number;
  pageNumber: number;
  pageSize: number;
  data: T[];
}

export type SortDirection = "asc" | "desc";
